import { Component, type ErrorInfo, type ReactNode } from "react";
import { captureException } from "../observability/sentry";

type Props = {
  children: ReactNode;
  /**
   * Qué mostrar si un hijo tira durante el render. Si es una función, recibe un
   * `reset` para reintentar. Si se omite, se usa un fallback genérico con recarga.
   * Pasar `fallback={null}` para degradar en silencio (p. ej. un botón opcional).
   */
  fallback?: ReactNode | ((reset: () => void) => ReactNode);
  /**
   * Cuando este valor cambia, el boundary se OLVIDA del error y vuelve a
   * intentar renderizar a sus hijos. Sirve para "un error de esta pantalla no
   * se queda pegado al navegar a otra".
   *
   * Existe en vez de `key={pathname}`: la key también resetea, pero DESMONTA
   * todo el árbol en cada navegación —providers, layouts y su estado—, así que
   * cambiar de pestaña recargaba la pantalla entera. Esto resetea el error sin
   * tocar la identidad de los hijos.
   */
  resetKey?: unknown;
};

type State = { hasError: boolean };

/**
 * Error boundary genérico. Evita que un error de render de una parte tire toda
 * la app (pantalla en blanco). Se usa a nivel de ruta (fallback con recarga) y
 * alrededor de widgets externos frágiles como el botón de Google Sign-In
 * (fallback null → simplemente no aparece).
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidUpdate(prev: Props) {
    if (this.state.hasError && prev.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false });
    }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Log para diagnóstico; no rompemos la UI.
    console.error("[ErrorBoundary]", error, info.componentStack);
    // Y además a Sentry: una pantalla en blanco es justo el error que el
    // usuario NO reporta (recarga, se encoge de hombros y sigue). El
    // componentStack dice qué componente reventó, no sólo dónde tiró el JS.
    captureException(error, { componentStack: info.componentStack });
  }

  reset = () => this.setState({ hasError: false });

  render() {
    if (!this.state.hasError) return this.props.children;

    const { fallback } = this.props;
    if (fallback !== undefined) {
      return typeof fallback === "function" ? fallback(this.reset) : fallback;
    }

    return (
      <div className="max-w-md mx-auto mt-16 text-center animate-slide-up">
        <div className="rounded-xl border border-forja-border bg-forja-surface p-6 shadow-card">
          <h2 className="text-lg font-bold mb-2">Algo salió mal</h2>
          <p className="text-sm text-white/60 mb-4">
            Ocurrió un error inesperado en esta pantalla. Podés recargar para intentar de nuevo.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="inline-flex items-center justify-center rounded-lg bg-forja-blue px-4 py-2 text-sm font-semibold text-white hover:bg-forja-blue/90 transition-colors"
          >
            Recargar
          </button>
        </div>
      </div>
    );
  }
}
